import {Component, inject, OnInit, OnDestroy, HostListener, effect, ErrorHandler} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {MatListModule} from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { NgFor, NgIf } from '@angular/common';
import {Subject} from "rxjs";
import {PropertiesDialogDataSelect} from '@llm/service/utils_main_comp_variables.service';
import {llmStoreService} from '@llm/service/llm_store.service';
import {MicrophoneService} from '@serv/media_access/api_web_access_microphone.service';
import * as UtilityMediaAccess from '@serv/media_access/utility';
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";
import {CustomErrorHandler} from "@llm/service/custom-error-handler";
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

/**
 * Componente de diálogo para selección de datos con reconocimiento de voz
 *
 * Este componente permite:
 * 1. Mostrar una lista de datos en formato de diálogo
 * 2. Seleccionar datos mediante comandos de voz
 * 3. Seleccionar datos mediante teclado numerico
 * 4. Interpretar números en diferentes formatos (verbal, ordinal, numérico)
 * 5. Manejar casos especiales como "último", "penúltimo"
 * 6. Proporcionar feedback visual del estado de reconocimiento
 *
 * @selector 'mkg-llm-data-select'
 */
@Component({
  selector: 'mkg-llm-data-select',
  standalone: true,
  imports: [MatDialogTitle, MatDialogContent, MatListModule, MatIconModule, NgFor, NgIf, FormsModule, MatFormFieldModule, MatInputModule],
  templateUrl: './data-select.component.html',
  styleUrl: './data-select.component.scss',
    providers:[
        { provide: ErrorHandler, useClass: CustomErrorHandler }
    ]
})
export class DataSelectComponent implements OnInit, OnDestroy {
  private readonly CONSUMER_ID: UtilityMediaAccess.MicrophoneConsumer = 'DATA_SELECT_OPTION_DETECTOR';

  /**
   *Injeccion de servicios necesarios para el funcionamineto del dialogComponent
   */
  store = inject(llmStoreService)
  private _console = inject(Tools_aiConsoleService);
  private _accessMicSvc = inject(MicrophoneService);
  protected searchTerm: string ='';

  /**
   * Referencia al diálogo para controlar su cierre
   * @returns {string | undefined} ID del cliente seleccionado al cerrar
   */
  readonly dialogRef = inject(MatDialogRef<DataSelectComponent, string | undefined>);

  /**
   * Datos inyectados en el diálogo con la lista de elementos<datos>
   */
  ObjData: PropertiesDialogDataSelect = inject(MAT_DIALOG_DATA);
  /**
   * Bandera para controlar la animación de cambio de mensaje
   */
  messageChanged = false;

  /**
   * Mensaje de error a mostrar al usuario
   */
  errorMessage = '';
  /**
   * Número del cliente seleccionado (1-based)
   */
  selectedNumber: number | null = null;
  /**
   * Subject utilizado para gestionar la desuscripción de Observables,
   * garantizando que no haya fugas de memoria al destruir el componente.
   */
  private destroy$ = new Subject<void>();

  constructor() {
    effect(() => {
      // Solo procesamos si somos el consumidor activo
      if (this._accessMicSvc.isConsumerActive(this.CONSUMER_ID)) {
        const transcript = this._accessMicSvc.transcriptionSession();
        if (transcript && !this.selectedNumber) {
          this.processResult(transcript);
          if (this.ObjData.references) {
            this.processReferenceBoolean(transcript);
          }
        }
      }
    });
  }

  /**
   * Inicializa el componente.
   * Inicia la escucha de voz a través del servicio y se suscribe a sus resultados.
   */
  ngOnInit() {
   this._console.log("this.ObjData:",this.ObjData)
    this._accessMicSvc.requestLock(this.CONSUMER_ID);
  }

  /**
   * Limpia recursos al destruir el componente.
   * Limpia el signal de `transcriptionSession` del servicio `commandSpeechService`
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this._accessMicSvc.releaseLock(this.CONSUMER_ID);
  }

  private processReferenceBoolean(transcript: string) {
    const preserveChars = "çáéíóúâêîôûãõàèìòùäëïöü";
    const regex = new RegExp(`[^\w\s${preserveChars}]`, "g");
    const _transcript = transcript
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(regex, "");

    if (_transcript.includes(this.ObjData.references?.positive)) {
      this.selectData(this.ObjData.items[0].id)
    } else if (_transcript.includes(this.ObjData.references?.negative)) {
      this.onNoClick()
    }
  }

  /**
   * Procesa el resultado del reconocimiento de voz
   *
   * 1. Normaliza el texto reconocido
   * 2. Intenta interpretar el comando mediante:
   *    - Casos especiales (último, penúltimo)
   *    - Patrones regulares (número 1, opción 2)
   *    - Mapa numérico (um, dois, primeiro)
   * 3. Valida que el número esté en rango
   * 4. Selecciona el cliente correspondiente o muestra error
   *
   * @param event Evento de resultado del reconocimiento
   */
  private processResult(event: string) {

    const preserveChars = "çáéíóúâêîôûãõàèìòùäëïöü";
    const regex = new RegExp(`[^\w\s${preserveChars}]`, "g");
    const transcript = event
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(regex, "");
   this._console.log('Transcrição:', transcript);

    /**
     * Mapa de palabras numéricas a valores
     * @type {Object.<string, number>}
     */
    const numberMap: {[key: string]: number} = {
      'zero': 0, 'um': 1, 'dois': 2, 'tres': 3, 'quatro': 4, 'cinco': 5,
      'seis': 6, 'sete': 7, 'oito': 8, 'nove': 9, 'dez': 10,
      'onze': 11, 'doze': 12, 'treze': 13, 'catorze': 14, 'quinze': 15,
      'dezesseis': 16, 'dezessete': 17, 'dezoito': 18, 'dezenove': 19, 'vinte': 20,
      'primeiro': 1, 'primeira': 1, 'segundo': 2, 'segunda': 2,
      'terceiro': 3, 'terceira': 3, 'quarto': 4, 'quarta': 4,
      'quinto': 5, 'quinta': 5, 'sexto': 6, 'sexta': 6,
      'setimo': 7, 'setima': 7, 'oitavo': 8, 'oitava': 8,
      'nono': 9, 'nona': 9, 'decimo': 10, 'decima': 10,
      'meia': 6,
      'meio': 6
    };

    /**
     * Casos especiales para posiciones relativas
     * @type {Object.<string, function(): number>}
     */
    const specialCases: {[key: string]: () => number} = {
      'ultimo': () => this.ObjData.items.length,
      'ultima': () => this.ObjData.items.length,
      'penultimo': () => this.ObjData.items.length > 1 ? this.ObjData.items.length - 1 : 1,
      'penultima': () => this.ObjData.items.length > 1 ? this.ObjData.items.length - 1 : 1,
      'antepenultimo': () => this.ObjData.items.length > 2 ? this.ObjData.items.length - 2 : 1,
      'antepenultima': () => this.ObjData.items.length > 2 ? this.ObjData.items.length - 2 : 1
    };

    /**
     * Patrones de expresión regular para detectar números
     * @type {RegExp[]}
     */
    const patterns = [
      /(?:numero|opcao|cliente)\s*(\d+)/i,
      /(\d+)(?:º|ª|ro|ra)?/i,
      /^(?:escolha|selecione|pega)\s*(\d+)/i
    ];


    const words = transcript.split(/\s+/);
    let selectedNumber: number | null = null;

    for (const [key, fn] of Object.entries(specialCases)) {
      if (transcript.includes(key)) {
        selectedNumber = fn();
        break;
      }
    }

    // Buscar mediante patrones regulares
    if (!selectedNumber) {
      for (const pattern of patterns) {
        const match = transcript.match(pattern);
        if (match && match[1]) {
          selectedNumber = parseInt(match[1], 10);
          break;
        }
      }
    }

    // Buscar en mapa numérico
    if (!selectedNumber) {
      for (const word of words) {
        if (numberMap[word]) {
          selectedNumber = numberMap[word];
          break;
        }
      }
    }

    // Procesar el número seleccionado
    if (selectedNumber !== null) {
      if (selectedNumber > 0 && selectedNumber <= this.ObjData.items.length) {
        this.selectedNumber = selectedNumber;
        this.selectData(this.ObjData.items[selectedNumber - 1].id);
      } else {
        this.errorMessage = `Número ${selectedNumber} fora do intervalo (1-${this.ObjData.items.length}). Por favor, tente novamente.`;
      }
    } else {
      //this.errorMessage = 'Número não reconhecido. Diga "1", "um" ou "primeiro"';
    }
  }

  /**
   * Escucha eventos de teclado a nivel de documento para la selección numérica.
   * @param event El evento de teclado.
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const numKey = parseInt(event.key, 10);
    if (!isNaN(numKey) && numKey >= 1 && numKey <= 9) {
      if (numKey <= this.ObjData.items.length) {
        this.selectedNumber = numKey;
        this.selectData(this.ObjData.items[numKey - 1].id);
        event.preventDefault();
      } else {
        this.errorMessage = `Número ${numKey} fora do rango (1-${this.ObjData.items.length}).`;
        this._console.warn(this.errorMessage);
      }
    }
  }


  get currentMessage(): string {
    if (this.errorMessage) {
      return this.errorMessage;
    }
    if (this.store.isListening()) {
      return `Diga o número do ${this.ObjData?.nameClass || 'item'}`;
    }
    return 'Iniciando reconhecimento...';
  }

  /**
   *Devuelve la lista de items filtrada basada en el searchTerm.
   *Este getter se recalcula automáticamente cuando 'searchTerm' cambia.
   */
   get filteredItems(){
      if(!this.searchTerm){
          return this.ObjData.items
      }
      const lowerCaseSearchTerm = this.searchTerm.toLowerCase();

      return this.ObjData.items.filter(item =>{
          const nameMatch = item.name.toLowerCase().includes(lowerCaseSearchTerm);
          const descriptorMatch = item.descriptor ? item.descriptor.toLowerCase().includes(lowerCaseSearchTerm) : false;
          return nameMatch || descriptorMatch;
      });
   }

  /**
   * Selecciona un dato y cierra el diálogo.
   * @param dataId ID del cliente/dato seleccionado.
   */
  selectData(dataId: string): void {
    this.dialogRef.close(dataId);
  }

  /**
   * Maneja el cierre del diálogo sin selección
   * Activa la animación de cambio de mensaje
   */
  onNoClick(): void {
    this.dialogRef.close(undefined);
    this.messageChanged = true;
    setTimeout(() => this.messageChanged = false, 300);
  }
}
